import React from 'react';
import { useNavigate } from 'react-router-dom';
//import { useAuth } from './contexts/AuthContext';
import { Zap, Sparkles, LogOut, ArrowRight, MessageSquare, Shield, Mic } from 'lucide-react';
import { useQuery, useAction,useMutation } from 'convex/react';
import { api } from './convex/_generated/api';

export default function IndexMeal2() {
  const navigate = useNavigate();
  const sessionId = localStorage.getItem('session_id') || '';
  //const sessionId = '';
  
  
  const user = useQuery(api.users.getUserBySession,
    sessionId ? { sessionId } : "skip"
  );
  const logoutUser = useAction(api.mfa.logoutUser);

  const handleSignOut = async () => {
    if (user) {
      await logoutUser({ sessionId });
      localStorage.removeItem('session_id');
      navigate('/');
    } else {
      navigate('/auth');
    }
  };

  const features = [
    {
      icon: <MessageSquare size={24} color="#fff" />,
      title: 'AI Meal Ideas',
      description: 'Type what\'s in your kitchen and get meals you can make right now.',
    },
    {
      icon: <Shield size={24} color="#fff" />,
      title: 'Smart Grocery Lists',
      description: 'Auto-built from your weekly plan, minus what you already have. 4 categories, zero guesswork.',
    },
    {
      icon: <Mic size={24} color="#fff" />,
      title: 'Waste Savings',
      description: 'Track how much you save by cooking from your pantry instead of the store. Watch your streak grow.',
    },
  ];

return (
    <div style={S.page}>
      <div style={S.glow1} />
      <div style={S.glow2} />

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        .feature-card:hover { border-color: #FF7A45 !important; transform: translateY(-6px); box-shadow: 0 20px 40px rgba(0,0,0,0.08) !important; }
        .cta-btn:hover { transform: translateY(-2px); box-shadow: 0 14px 30px rgba(255,122,69,0.35); }
        .stat-card:hover { border-color: #FF7A45 !important; transform: translateY(-4px); }
      `}</style>

      {/* Header */}
      <header style={S.header}>
        <div style={S.headerInner}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={S.logoIcon}><Zap size={16} color="#fff" /></div>
            <span style={S.logoText}>MealPilot</span>
            <span style={S.logoBeta}>BETA</span>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button onClick={handleSignOut} style={S.signOutBtn}>
              <LogOut size={14} />
              {user ? 'Sign Out' : 'Sign In'}
            </button>
            <button onClick={() => navigate(user ? '/profile' : '/auth')} style={S.navBtn}>
              {user ? 'Profile Dashboard →' : 'Get Started →'}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section style={S.hero}>
        <div style={{ ...S.badge, animation: "fadeUp 0.4s ease both" }}>
          <Zap size={14} color="#FF7A45" />
          <span>AI-Powered</span>
        </div>

        <h1 style={{ ...S.heroTitle, animation: "fadeUp 0.5s ease both" }}>
          Plan Meals From What<br />
          <em style={{ fontStyle: "normal", background: "linear-gradient(90deg, #FF7A45, #FFB443)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            You Already Have
          </em>
        </h1>

        <p style={{ ...S.heroSubtitle, animation: "fadeUp 0.6s ease both" }}>
          Tell us what's in your kitchen. Our AI builds your weekly meal plan,<br />
          writes your grocery list, and tracks your pantry — automatically.
        </p>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginBottom: 40, animation: "fadeUp 0.7s ease both" }}>
          {["🍳 What Can I Make?", "🧾 Grocery Lists", "📈 Waste Savings" ].map((s, i) => (
            <span key={i} style={S.tag}>{s}</span>
          ))}
        </div>

        <button
          className="cta-btn"
          onClick={() => navigate((user?.role === 'premium')   ? '/profile' : '/auth')}
          style={{ ...S.ctaBtn, animation: "fadeUp 0.8s ease both" }}
        >
         {user?.role === 'premium' ? 'Open Dashboard' : 'Start Free Today'} <ArrowRight size={18} />
        </button>

        <p style={{ fontSize: 13, color: "#A89684", marginTop: 12 }}>
          No credit card required · Ready in under 60 seconds
        </p>
      </section>

      {/* Stats */}
      <section style={S.stats}>
        {[
          ["7-Day", "Meal Plans"],
          ["AI", "Recipe Ideas"],
          ["Auto", "Grocery Lists"],
          ["24/7", "Pantry Tracking"],
        ].map(([n, l], i) => (
          <div key={i} className="stat-card" style={S.statCard}>
            <strong style={S.statNum}>{n}</strong>
            <span style={S.statLabel}>{l}</span>
          </div>
        ))}
      </section>

      {/* Features */}
      <section style={S.features}>
        <h2 style={S.sectionTitle}>Everything in one platform</h2>
        <p style={S.sectionSubtitle}>From pantry to plate — fully automated</p>
        <div style={S.featureGrid}>
          {features.map((f, i) => (
            <div key={i} className="feature-card" style={{ ...S.featureCard, transition: "all 0.2s" }}>
              <div style={S.featureIcon}>{f.icon}</div>
              <h3 style={S.featureTitle}>{f.title}</h3>
              <p style={S.featureText}>{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={S.howItWorks}>
        <h2 style={S.sectionTitle}>How it works</h2>
        <p style={S.sectionSubtitle}>Three steps to a full fridge and an empty waste bin</p>
        <div style={S.stepsGrid}>
          {[
            { step: "01", title: "Add Your Pantry", desc: "Tell us what's in your kitchen, your household size, and your dietary needs." },
            { step: "02", title: "AI Builds Your Plan", desc: "Claude AI generates a 7-day meal plan that reuses ingredients and fits your budget." },
            { step: "03", title: "Cook & Save", desc: "Your grocery list is ready automatically. Mark meals as cooked and watch your savings grow." },
          ].map((s, i) => (
            <div key={i} style={S.stepCard}>
              <div style={S.stepNumber}>{s.step}</div>
              <h3 style={S.stepTitle}>{s.title}</h3>
              <p style={S.stepDesc}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section style={S.pricing}>
        <h2 style={S.sectionTitle}>Simple pricing</h2>
        <p style={S.sectionSubtitle}>One plan. Everything included.</p>
        <div style={S.pricingCard}>
          <div style={S.pricingBadge}>MOST POPULAR</div>
          <div style={S.price}>$99<span style={S.pricePer}>/month</span></div>
          <p style={S.pricingDesc}>Everything you need to automate your meal planning</p>
          <div style={S.pricingFeatures}>
            {[
              "AI Meal Suggestions",
              "Weekly Meal Plans",
              "Smart Grocery Lists",
              "Waste Savings Dashboard",
              "Unlimited Households",
              "Cancel Anytime",
            ].map((f, i) => (
              <div key={i} style={S.pricingFeature}>{f}</div>
            ))}
          </div>
          <button
            className="cta-btn"
            onClick={() => navigate(user?.role === 'premium' ? '/profile' : '/auth')}
            style={{ ...S.ctaBtn, width: "100%", justifyContent: "center" }}
          >
            Get Started <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* CTA */}
      <section style={S.ctaSection}>
        <h2 style={S.ctaTitle}>Ready to stop wasting food?</h2>
        <p style={S.ctaSubtitle}>
          Join households using AI to plan meals and cut grocery waste automatically.
        </p>
        <button
          className="cta-btn"
          onClick={() => navigate(user ? '/profile' : '/auth')}
          style={S.ctaBtn}
        >
          {user ? 'Open Dashboard' : 'Start Free Today'} <ArrowRight size={18} />
        </button>
      </section>

      {/* Footer */}
      <footer style={S.footer}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 8 }}>
          <div style={S.logoIcon}><Zap size={14} color="#fff" /></div>
          <span style={{ ...S.logoText, fontSize: 16 }}>MealPilot</span>
        </div>
        <p style={{ fontSize: 13, color: "#A89684" }}>AI-powered meal planning</p>
        <div style={{ marginTop: 12, display: "flex", gap: 16, justifyContent: "center" }}>
          <a href="/privacy" style={{ fontSize: 12, color: "#8A7A6C", textDecoration: "none" }}>Privacy Policy</a>
          <a href="/terms" style={{ fontSize: 12, color: "#8A7A6C", textDecoration: "none" }}>Terms of Service</a>
        </div>
      </footer>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    background: "#FFF9F2",
    color: "#2B2118",
    fontFamily: "'Poppins', 'Inter', sans-serif",
    position: "relative",
    overflow: "hidden",
  },
  glow1: {
    position: "fixed",
    top: -150, right: -150,
    width: 600, height: 600,
    background: "rgba(255,122,69,0.10)",
    borderRadius: "50%",
    filter: "blur(150px)",
    pointerEvents: "none",
  },
  glow2: {
    position: "fixed",
    bottom: -150, left: -150,
    width: 600, height: 600,
    background: "rgba(255,180,67,0.12)",
    borderRadius: "50%",
    filter: "blur(150px)",
    pointerEvents: "none",
  },
  header: {
    borderBottom: "1px solid #F0E4D6",
    padding: "0 24px",
    position: "sticky",
    top: 0,
    background: "rgba(255,249,242,0.9)",
    backdropFilter: "blur(16px)",
    zIndex: 100,
  },
  headerInner: {
    maxWidth: 1100,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 64,
  },
  logoIcon: {
    width: 32, height: 32,
    borderRadius: 10,
    background: "linear-gradient(135deg, #FF7A45, #FFB443)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoText: {
    fontSize: 20,
    fontWeight: 700,
    color: "#2B2118",
    letterSpacing: "-0.02em",
  },
  logoBeta: {
    fontSize: 10,
    fontWeight: 700,
    color: "#FF7A45",
    border: "1px solid #FF7A45",
    borderRadius: 4,
    padding: "2px 6px",
    letterSpacing: "0.08em",
  },
  navBtn: {
    background: "linear-gradient(135deg, #FF7A45, #FFB443)",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    padding: "9px 18px",
    borderRadius: 10,
  },
  signOutBtn: {
    background: "#fff",
    border: "1px solid #F0E4D6",
    color: "#8A7A6C",
    cursor: "pointer",
    fontSize: 13,
    padding: "8px 14px",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  hero: {
    maxWidth: 760,
    margin: "0 auto",
    padding: "120px 24px 80px",
    textAlign: "center",
    position: "relative",
    zIndex: 1,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#FF7A45",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 24,
    background: "#FFF1E6",
    padding: "6px 14px",
    borderRadius: 20,
    border: "1px solid #FFD9B8",
    fontWeight: 700,
  },
  heroTitle: {
    fontSize: "clamp(42px, 7vw, 72px)",
    fontWeight: 800,
    lineHeight: 1.05,
    color: "#2B2118",
    marginBottom: 20,
    letterSpacing: "-0.03em",
  },
  heroSubtitle: {
    fontSize: 18,
    color: "#8A7A6C",
    lineHeight: 1.75,
    marginBottom: 32,
  },
  tag: {
    fontSize: 12,
    fontWeight: 600,
    color: "#8A5A3C",
    border: "1px solid #FFD9B8",
    borderRadius: 20,
    padding: "5px 14px",
    background: "#FFF1E6",
  },
  ctaBtn: {
    background: "linear-gradient(135deg, #FF7A45, #FFB443)",
    color: "#fff",
    border: "none",
    borderRadius: 14,
    padding: "16px 40px",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    transition: "all 0.2s",
    boxShadow: "0 8px 20px rgba(255,122,69,0.25)",
  },
  stats: {
    display: "flex",
    justifyContent: "center",
    gap: 24,
    flexWrap: "wrap",
    padding: "48px 24px",
    borderTop: "1px solid #F0E4D6",
    borderBottom: "1px solid #F0E4D6",
    position: "relative",
    zIndex: 1,
  },
  statCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    padding: "20px 32px",
    border: "1px solid #F0E4D6",
    borderRadius: 14,
    background: "#fff",
    boxShadow: "0 4px 14px rgba(43,33,24,0.04)",
    transition: "all 0.2s",
    minWidth: 120,
  },
  statNum: {
    fontSize: 32,
    fontWeight: 800,
    color: "#FF7A45",
    letterSpacing: "-0.02em",
  },
  statLabel: {
    fontSize: 11,
    color: "#A89684",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontWeight: 600,
  },
  features: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "100px 24px",
    textAlign: "center",
    position: "relative",
    zIndex: 1,
  },
  sectionTitle: {
    fontSize: 36,
    fontWeight: 800,
    color: "#2B2118",
    marginBottom: 12,
    letterSpacing: "-0.02em",
  },
  sectionSubtitle: {
    fontSize: 16,
    color: "#8A7A6C",
    marginBottom: 56,
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 24,
  },
  featureCard: {
    background: "#fff",
    border: "1px solid #F0E4D6",
    borderRadius: 18,
    padding: "36px 28px",
    textAlign: "left",
    boxShadow: "0 4px 14px rgba(43,33,24,0.04)",
  },
  featureIcon: {
    width: 52,
    height: 52,
    borderRadius: 16,
    background: "linear-gradient(135deg, #FF7A45, #FFB443)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  featureTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#2B2118",
    marginBottom: 10,
    letterSpacing: "-0.01em",
  },
  featureText: {
    fontSize: 14,
    color: "#8A7A6C",
    lineHeight: 1.75,
  },
  howItWorks: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "0 24px 100px",
    textAlign: "center",
    position: "relative",
    zIndex: 1,
  },
  stepsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 24,
  },
  stepCard: {
    background: "#fff",
    border: "1px solid #F0E4D6",
    borderRadius: 18,
    padding: "36px 28px",
    textAlign: "left",
    boxShadow: "0 4px 14px rgba(43,33,24,0.04)",
  },
  stepNumber: {
    fontSize: 48,
    fontWeight: 800,
    color: "#FFD9B8",
    lineHeight: 1,
    marginBottom: 16,
    letterSpacing: "-0.03em",
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#2B2118",
    marginBottom: 8,
  },
  stepDesc: {
    fontSize: 14,
    color: "#8A7A6C",
    lineHeight: 1.75,
  },
  pricing: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "0 24px 100px",
    textAlign: "center",
    position: "relative",
    zIndex: 1,
  },
  pricingCard: {
    background: "#fff",
    border: "2px solid #FF7A45",
    borderRadius: 22,
    padding: "40px 36px",
    position: "relative",
    boxShadow: "0 10px 30px rgba(255,122,69,0.12)",
  },
  pricingBadge: {
    position: "absolute",
    top: -12,
    left: "50%",
    transform: "translateX(-50%)",
    background: "linear-gradient(135deg, #FF7A45, #FFB443)",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    padding: "4px 14px",
    borderRadius: 20,
    letterSpacing: "0.1em",
  },
  price: {
    fontSize: 64,
    fontWeight: 800,
    color: "#2B2118",
    letterSpacing: "-0.03em",
    marginBottom: 8,
  },
  pricePer: {
    fontSize: 20,
    color: "#8A7A6C",
    fontWeight: 400,
  },
  pricingDesc: {
    fontSize: 14,
    color: "#8A7A6C",
    marginBottom: 28,
  },
  pricingFeatures: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 32,
    textAlign: "left",
  },
  pricingFeature: {
    fontSize: 14,
    color: "#5C4C3E",
    fontWeight: 500,
  },
  ctaSection: {
    textAlign: "center",
    padding: "80px 24px",
    borderTop: "1px solid #F0E4D6",
    position: "relative",
    zIndex: 1,
  },
  ctaTitle: {
    fontSize: 40,
    fontWeight: 800,
    color: "#2B2118",
    marginBottom: 16,
    letterSpacing: "-0.02em",
  },
  ctaSubtitle: {
    fontSize: 16,
    color: "#8A7A6C",
    marginBottom: 32,
    maxWidth: 500,
    margin: "0 auto 32px",
  },
  footer: {
    borderTop: "1px solid #F0E4D6",
    padding: "40px 24px",
    textAlign: "center",
  },
};